import React, { useCallback, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { usePermissions } from '@/app/hooks/usePermissions';
import { IntlContext } from '@/i18n';
import { prepareCarForm } from '@/modules/car/actions/async/prepare-car-form';
import {
  TUpdateCarLicensingForm,
  updateCarLicensing,
} from '@/modules/car/actions/async/updateCarLicensing';
import { getCarForm } from '@/modules/car/store/selectors';
import { AppDispatch } from '@/root-store';
import { CountryCode } from '@/shared/models/country-code';
import { useForm } from '@/shared/view/Form';

import { EditVehicleLicensingView } from './view';

interface IProps {
  carId: string;
  onNavigateBack: (uri: string) => void;
}

const EditVehicleLicensing: React.FC<IProps> = ({ carId, onNavigateBack }) => {
  const dispatch = useDispatch() as AppDispatch;

  usePermissions('viewCars');

  const { region } = useContext(IntlContext);
  const carForm = useSelector(getCarForm);
  const isFormReady = carForm.id === carId;

  const navigateBack = useCallback(
    () => onNavigateBack(`/vehicles/${carId}`),
    [carId, onNavigateBack],
  );

  const postForm = useCallback(
    async (formValues: TUpdateCarLicensingForm) => {
      const { isSucceed, result = {} } = await dispatch(
        updateCarLicensing({ carId, formValues }),
      ).unwrap();

      if (isSucceed) {
        navigateBack();
      }

      return result;
    },
    [carId, dispatch, navigateBack],
  );

  const {
    handleSubmit,
    form: {
      control,
      formState: { isSubmitting },
      resetField,
    },
  } = useForm<TUpdateCarLicensingForm>({
    onSubmit: postForm,
    defaultValues: {
      licensingAuthority: carForm.licensingAuthority,
      licenseNumber: carForm.licenseNumber,
    },
  });

  useEffect(() => {
    dispatch(prepareCarForm({ carId }));
  }, [carId, dispatch]);

  useEffect(() => {
    resetField('licensingAuthority', { defaultValue: carForm.licensingAuthority });
    resetField('licenseNumber', { defaultValue: carForm.licenseNumber });
  }, [resetField, carForm.licensingAuthority, carForm.licenseNumber]);

  if (region !== CountryCode.GreatBritain || !isFormReady) {
    return null;
  }

  return (
    <EditVehicleLicensingView
      control={control}
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit}
      onNavigateBack={navigateBack}
    />
  );
};

export { EditVehicleLicensing };
